import { Component, Input, Output, EventEmitter, ViewChild, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import { TranslocoService } from '@jsverse/transloco';
import { DataExchangeSubRequestDto } from 'src/app/data-transfer/entities/data-exchange-request-entities/data-exchange-sub-request-dto';
import { DataExchangeRequestStatusDto } from 'src/app/data-transfer/entities/data-exchange-request-entities/data-exchange-request-status-dto';
import { PaginationDataSource } from 'src/app/util/pagination-datasource';
import { DataExchangeSubRequestPaginationFields } from 'src/app/data-transfer/entities/pagination-fields';
import { COMPONENT_CATEGORIES, ComponentTypeService } from 'src/app/navigation/services/component-type-service';
import { PackagingUnitTypeService } from 'src/app/navigation/services/packaging-unit-type-service';

@Component({
  selector: 'app-data-exchange-sub-request-table',
  templateUrl: './data-exchange-sub-request-table.component.html',
  styleUrls: ['./data-exchange-sub-request-table.component.scss'],
})
export class DataExchangeSubRequestTableComponent implements AfterViewInit {
  readonly requestStatus = DataExchangeRequestStatusDto;

  @Input() dataSource!: PaginationDataSource<DataExchangeSubRequestDto>;
  @Input() dataExchangeRequestId!: number;
  @Input() isReceived = false;

  @Output() navigateToSubRequest = new EventEmitter<DataExchangeSubRequestDto>();
  @Output() approveSubRequest = new EventEmitter<DataExchangeSubRequestDto>();
  @Output() reopenSubRequest = new EventEmitter<DataExchangeSubRequestDto>();
  @Output() withdrawSubRequest = new EventEmitter<DataExchangeSubRequestDto>();
  @Output() showSubRequestComments = new EventEmitter<DataExchangeSubRequestDto>();

  @ViewChild(MatPaginator) paginator!: MatPaginator;

  filteredColumns: string[] = [];
  isLoading = this.dataSource?.isLoading;

  constructor(
    private translocoService: TranslocoService,
    private componentTypeService: ComponentTypeService,
    private packagingUnitTypeService: PackagingUnitTypeService,
    private cd: ChangeDetectorRef
  ) {}

  ngAfterViewInit(): void {
    this.isLoading = this.dataSource.isLoading;
    this.dataSource.filteredColumns.subscribe(filteredColumns => {
      this.filteredColumns = filteredColumns;
    });
    this.dataSource.page.subscribe(page => {
      this.paginator.length = page.totalItemCount;
      this.paginator.pageIndex = page.pageIndex;
    });
    this.dataSource.fetchData(this.paginator.pageIndex, this.paginator.pageSize, [
      { fieldName: DataExchangeSubRequestPaginationFields.DataExchangeRequestId, values: [this.dataExchangeRequestId.toString()] },
    ]);
    this.cd.detectChanges();
  }

  onPageChanged(event: PageEvent): void {
    this.dataSource.fetchData(event.pageIndex, event.pageSize);
  }

  onSortChanged(event: Sort): void {
    if (event.direction) {
      this.dataSource.sort(this.getCaller(event.active), event.direction === 'asc');
    } else {
      this.dataSource.removeSorting();
    }
  }

  getCaller(column: string): string {
    switch (column) {
      case 'id':
        return DataExchangeSubRequestPaginationFields.Id;
      case 'articleName':
        return DataExchangeSubRequestPaginationFields.ArticleName;
      case 'productName':
        return DataExchangeSubRequestPaginationFields.ProductName;
      case 'brandName':
        return DataExchangeSubRequestPaginationFields.BrandName;
      case 'articleNumber':
        return DataExchangeSubRequestPaginationFields.ArticleNumber;
      case 'manufacturer':
        return DataExchangeSubRequestPaginationFields.Manufacturer;
      case 'packagingComponentType':
        return DataExchangeSubRequestPaginationFields.PackagingComponentType;
      case 'packagingType':
        return DataExchangeSubRequestPaginationFields.PackagingType;
      case 'status':
        return DataExchangeSubRequestPaginationFields.Status;
      case 'lastModificationTimestamp':
        return DataExchangeSubRequestPaginationFields.LastModificationTimestamp;
      default:
        return '';
    }
  }

  getArticleName(sub: DataExchangeSubRequestDto): string {
    return sub.packagingComponent?.articleName ?? sub.multiMaterial?.articleName ?? '';
  }

  getProductName(sub: DataExchangeSubRequestDto): string {
    return sub.packagingUnitInfo?.productName ?? sub.packagingSystemInfo?.productName ?? '';
  }

  getBrandName(sub: DataExchangeSubRequestDto): string {
    return sub.packagingUnitInfo?.brandName ?? sub.packagingSystemInfo?.brandName ?? '';
  }

  getArticleNumber(sub: DataExchangeSubRequestDto): string {
    return sub.packagingComponent?.articleNumber ?? sub.multiMaterial?.articleNumber ?? sub.packagingUnitInfo?.articleNumber ?? sub.packagingSystemInfo?.articleNumber ?? '';
  }

  getManufacturer(sub: DataExchangeSubRequestDto): string {
    return sub.packagingComponent?.manufacturer ?? sub.multiMaterial?.manufacturerName ?? '';
  }

  getPackagingComponentType(sub: DataExchangeSubRequestDto): string {
    return sub.packagingComponent?.packagingComponentCategoryName ?? '';
  }

  getPackagingComponentTypeImage(sub: DataExchangeSubRequestDto): string | null {
    return sub.packagingComponent ? this.componentTypeService.getComponentTypeImage(sub.packagingComponent.packagingComponentCategoryId) : null;
  }

  getPackagingComponentTypeOptions(): { name: string; value: number }[] {
    return COMPONENT_CATEGORIES.map(category => ({ name: this.translocoService.translate(`component.${category.label}`), value: category.key }));
  }

  getPackagingTypeImage(sub: DataExchangeSubRequestDto): string | null {
    return sub.packagingUnitInfo ? this.packagingUnitTypeService.getPackagingUnitTypeImage(sub.packagingUnitInfo.packagingTypeId) : null;
  }

  getPackagingTypeOptions(): { name: string; value: number }[] {
    return this.packagingUnitTypeService.getPackagingUnitTypes().map(type => ({ name: type.name, value: type.id }));
  }

  getPackagingType(sub: DataExchangeSubRequestDto): string {
    return (sub.packagingUnitInfo as any)?.packagingTypeName ?? '';
  }

  getDate(dateStr: string): Date | null {
    if (dateStr == null) {
      return null;
    }
    return new Date(dateStr);
  }

  getStatusLabel(status: DataExchangeRequestStatusDto): string {
    const key = DataExchangeRequestStatusDto[status];
    return `dataManagement.dataExchangeRequests.status.${key}`;
  }

  getStatusOptions(): { name: string; value: number }[] {
    return Object.values(DataExchangeRequestStatusDto)
      .filter((value): value is number => typeof value === 'number')
      .map(status => ({ name: this.translocoService.translate(this.getStatusLabel(status)), value: status }));
  }

  containsMultiMaterials() {
    return this.dataSource.data.filter(x => x.multiMaterial).length > 0;
  }

  containsComponents() {
    return this.dataSource.data.filter(x => x.packagingComponent).length > 0;
  }

  containsPackagingUnits() {
    return this.dataSource.data.filter(x => x.packagingUnitInfo).length > 0;
  }

  containsPackagingSystems() {
    return this.dataSource.data.filter(x => x.packagingSystemInfo).length > 0;
  }

  private isColumnRelevant(column: string, hasRelevantData: boolean): boolean {
    return hasRelevantData || this.filteredColumns.includes(this.getCaller(column));
  }

  get displayedColumns(): string[] {
    const columns = ['id'];
    if (this.isColumnRelevant('packagingComponentType', this.containsComponents())) {
      columns.push('packagingComponentType');
    }
    if (this.isColumnRelevant('packagingType', this.containsPackagingUnits())) {
      columns.push('packagingType');
    }
    if (this.isColumnRelevant('articleName', this.containsComponents() || this.containsMultiMaterials())) {
      columns.push('articleName');
    }
    if (this.isColumnRelevant('brandName', this.containsPackagingUnits() || this.containsPackagingSystems())) {
      columns.push('brandName');
    }
    if (this.isColumnRelevant('productName', this.containsPackagingSystems() || this.containsPackagingUnits())) {
      columns.push('productName');
    }
    columns.push('articleNumber');
    if (this.isColumnRelevant('manufacturer', this.containsMultiMaterials())) {
      columns.push('manufacturer');
    }
    columns.push('status', 'lastModificationTimestamp', 'showSubRequestComments', 'actions');
    return columns;
  }
}
